import React from 'react';

import { useTranslation } from 'react-i18next';

import { M } from 'components/typography';
import { PopupMenu, TextButton } from 'design-system-web';
import { useGetActionsForChargeCodes } from './useGetActionsForChargeCodes';
import { useBillingAndPaymentsServicesCRUDEnabled } from 'react-hooks';
import { ChargeCode } from 'graphql-query-contracts';

interface TableColumn {
  key: string;
  header: string;
  renderCell: (cell: string, row: ChargeCode | null) => React.ReactNode;
  disableSorting: boolean;
}

export function useChargeCodesTableColumns(onDelete: (chargeCode: ChargeCode) => void): TableColumn[] {
  const { t } = useTranslation();
  const chargeCodeActions = useGetActionsForChargeCodes({ onDelete });
  const isBillingAndPaymentsServicesCRUDEnabled = useBillingAndPaymentsServicesCRUDEnabled();
  
  const chargeCodeTableColumns: TableColumn[] = [
    {
      key: 'chargeCode',
      header: t('abp.chargeCodes.chargeCode'),
      renderCell: renderStringCell,
      disableSorting: false,
    },
    {
      key: 'glCode',
      header: t('abp.chargeCodes.glCode'),
      renderCell: renderStringCell,
      disableSorting: false,
    },
    {
      key: 'description',
      header: t('abp.chargeCodes.description'),
      renderCell: renderStringCell,
      disableSorting: true,
    },
  ];

  const actionColumn: TableColumn = {
    key: 'actions',
    header: t('abp.actions'),
    disableSorting: true,
    renderCell: (_cell: string, row: ChargeCode | null) => {
      if (!row) {
        return null;
      }

      return (
        <div data-test="charge-code-actions">
          <PopupMenu
            trigger={
              <TextButton iconName="angle-down">{t('abp.actions')}</TextButton>
            }
            items={chargeCodeActions.map(({ label, onSelect }) => ({
              label,
              onSelect: () => {
                onSelect(row);
              },
              isDisabled: false,
            }))}
          />
        </div>
      );
    },
  };

  if (isBillingAndPaymentsServicesCRUDEnabled) {
    chargeCodeTableColumns.push(actionColumn);
  }

  return chargeCodeTableColumns;
}

function renderStringCell(cell: string, _row: ChargeCode | null) {
  return <M>{cell}</M>;
}
